/**
 * Per-instance animation hook for independent animation control.
 * Each call creates a separate animation instance with its own state.
 * Used for multiple animations per slide in presentations.
 */

import { useEffect, useReducer, useRef } from 'react'
import type { Drawing, Player } from '@/types'
import { animationReducer } from '../contexts/AnimationContext'
import type { AnimationState, PlaybackSpeed } from '../types/Animation.types'
import { initialAnimationState } from '../types/Animation.types'
import { useAnimationTiming } from './UseAnimationTiming'
import { useAnimationProgress } from './UseAnimationEngine'
import { useAnimationLoop } from './UseAnimationLoop'
import { useVisibilityPause } from './UseVisibilityPause'
import { useAnimationControls } from './useAnimationControls'

type PlayData = {
	id: string
	players: Player[]
	drawings: Drawing[]
}

type UseAnimationInstanceOptions = {
	/** Play data to animate (null = not loaded) */
	playData: PlayData | null
	/** Auto-play when data loads */
	autoPlay?: boolean
	/** Loop animation when complete */
	loop?: boolean
	/** Callback when animation completes (only fires if not looping) */
	onComplete?: () => void
}

type AnimationInstance = {
	/** Current animation state */
	state: AnimationState
	/** Start or resume playback */
	play: () => void
	/** Pause playback */
	pause: () => void
	/** Stop and reset to beginning */
	reset: () => void
	/** Toggle between play and pause */
	toggle: () => void
	/** Seek to position (0-1) */
	seek: (progress: number) => void
	/** Set playback speed */
	setSpeed: (speed: PlaybackSpeed) => void
	/** Toggle loop mode */
	toggleLoop: () => void
	/** Toggle ghost trail visibility */
	toggleGhostTrail: () => void
	/** Whether animation data is ready */
	isReady: boolean
	/** Whether currently playing */
	isPlaying: boolean
	/** Whether animation has completed */
	isComplete: boolean
	/** Progress as percentage (0-100) */
	progress: number
}

/**
 * Creates an independent animation instance with its own state.
 * Use this hook to manage animations for individual play blocks.
 */
export function useAnimationInstance({
										 playData,
										 autoPlay = false,
										 loop = false,
										 onComplete,
									 }: UseAnimationInstanceOptions): AnimationInstance {
	// Create isolated state for this instance
	const [state, dispatch] = useReducer(animationReducer, {
		...initialAnimationState,
		loopMode: loop,
	})

	// Calculate timing from play data
	const timingPayload = useAnimationTiming(playData)

	// Track refs for callbacks
	const onCompleteRef = useRef(onComplete)
	const autoPlayRef = useRef(autoPlay)
	const hasAutoPlayedRef = useRef(false)

	// Keep refs updated
	useEffect(() => {
		onCompleteRef.current = onComplete
	}, [onComplete])

	useEffect(() => {
		autoPlayRef.current = autoPlay
	}, [autoPlay])

	// Load play data when timing is calculated
	useEffect(() => {
		if (timingPayload) {
			dispatch({ type: 'LOAD_PLAY', payload: timingPayload })
			hasAutoPlayedRef.current = false // Reset auto-play flag for new data
		}
	}, [timingPayload])

	// Handle auto-play after loading
	useEffect(() => {
		if (
			autoPlayRef.current &&
			state.phase === 'ready' &&
			state.playId !== null &&
			!hasAutoPlayedRef.current
		) {
			hasAutoPlayedRef.current = true
			dispatch({ type: 'PLAY' })
		}
	}, [state.phase, state.playId])

	// Update loop mode when prop changes
	useEffect(() => {
		if (loop !== state.loopMode) {
			dispatch({ type: 'TOGGLE_LOOP' })
		}
	}, [loop, state.loopMode])

	// Use refs for state values read inside the animation loop
	// This avoids recreating the RAF loop when these values change
	const stateRef = useRef(state)
	useEffect(() => {
		stateRef.current = state
	}, [state])

	// Determine if animation loop should be active
	const isActive = state.isPlaying && state.phase === 'execution'

	// Use shared animation loop hook
	useAnimationLoop({
		stateRef,
		dispatch,
		onComplete: onCompleteRef.current,
		isActive,
	})

	// Pause animation when tab is hidden
	useVisibilityPause({ stateRef, dispatch })

	// Create control methods using shared hook
	const {
		play,
		pause,
		reset,
		toggle,
		seek,
		setSpeed,
		toggleLoop,
		toggleGhostTrail,
	} = useAnimationControls(dispatch, state.isPlaying)

	// Computed values
	const isReady = state.playId !== null && state.totalDuration > 0
	const isPlaying = state.isPlaying
	const isComplete = state.phase === 'complete'
	const progress = useAnimationProgress(state)

	return {
		state,
		play,
		pause,
		reset,
		toggle,
		seek,
		setSpeed,
		toggleLoop,
		toggleGhostTrail,
		isReady,
		isPlaying,
		isComplete,
		progress,
	}
}

/**
 * Type export for external use
 */
export type { AnimationInstance }
